import React, { useEffect, useState, ReactElement } from 'react';
import FormatService from '../../services/format-service';
import useStylesTableResize from './table-resize.styles';
import { TIMEOUT_DEFAULT } from '../../constants/system/timeouts';

const TableResize: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDTH_AUTO: string = 'auto';
  const HEIGHT_AUTO: string = 'auto';
  const VISIBILITY_HIDDEN: VisibilityValue = 'hidden';
  const VISIBILITY_VISIBLE: VisibilityValue = 'visible';
  const MIN_HEADER_CELL_WIDTH: number = 100;
  const MAX_HEADER_CELL_WIDTH: number = 1000;
  const MIN_HEADER_CELL_WIDTH_DISABLED: number = 300;
  const MOUSE_BUTTON_LEFT: number = 0;
  const RENDER_DELAY: number = 750;
  const GROUP_ID_DEFAULT: number = 0;

  const {
    isActive,
    isDisabled,
    tableRef,
    headerCellRefs,
    tableColumnSettings,
    groupId,
    minCellWidth,
    onTableColumnsChange,
    children,
  } = props;

  const { classes } = useStylesTableResize();

  const [isResize, setIsResize]: [boolean, Function] = useState(false);
  const [activeItemId, setActiveItemId]: [number | null, Function] = useState(null);
  const [activeItemOffset, setActiveItemOffset]: [number, Function] = useState(0);
  const [dividerOffsets, setDividerOffsets]: [Record<string, StructureItem>, Function] = useState({});
  const [tableWidth, setTableWidth]: [number | null, Function] = useState(null);
  const [dividerHeight, setDividerHeight]: [number, Function] = useState(0);
  const [tableVisibleColumns, setTableVisibleColumns]: [string[] | null, Function] = useState(null);
  const [currentActiveItemOffset, setCurrentActiveItemOffset]: [number, Function] = useState(0);
  const [currentDividerOffsets, setCurrentDividerOffsets]: [Record<string, StructureItem>, Function] = useState({});

  const tableElement: HTMLTableElement = tableRef && (groupId || groupId === GROUP_ID_DEFAULT
    ? tableRef.current[groupId] : tableRef.current);
  const headerCellElements: Record<string, HTMLElement> = headerCellRefs
    && (groupId || groupId === GROUP_ID_DEFAULT ? headerCellRefs.current[groupId] : headerCellRefs.current);
  const headerCellElementsTotal: number = (headerCellElements && Object.keys(headerCellElements).length) || 0;
  const isLoading: boolean = !isActive && !isDisabled && (!tableElement || !tableElement.style.width);

  const updateTableColumnSettings: Function = (): void => {
    const updatedTableColumnSettings: TableField[] = [];

    let hiddenIndex: number = 0;
    let previousPositionOffset: number = 0;
    tableColumnSettings.filter((field: TableField): boolean => !!field)
      .forEach((field: TableField, fieldIndex: number): void => {
        let tableColumnWidth: number | null = null;
        if (field.isVisible) {
          const offsetValue: string | null = Object.values(dividerOffsets)[fieldIndex - hiddenIndex] || null;
          const offsetNumberValue: number = offsetValue ? FormatService.parseInt(offsetValue) : 0;
          tableColumnWidth = offsetValue ? Math.floor(offsetNumberValue - previousPositionOffset) : null;
          previousPositionOffset = offsetNumberValue || previousPositionOffset;
        } else {
          hiddenIndex++;
        }

        updatedTableColumnSettings.push({
          ...field,
          width: tableColumnWidth || null,
        });
      });

    onTableColumnsChange(updatedTableColumnSettings);
  };

  const updateTableVisibleColumns: Function = (): void => {
    if (tableColumnSettings && tableColumnSettings.length) {
      const updatedTableVisibleColumns: string[] = tableColumnSettings
        .filter((tableField: TableField): boolean => !!tableField)
        .map((tableField: TableField): string | null => (
          tableField.isVisible ? tableField.name : null
        ))
        .filter((tableFieldName: string): boolean => !!tableFieldName);

      if (tableVisibleColumns !== updatedTableVisibleColumns) {
        setTableVisibleColumns(updatedTableVisibleColumns);
      }
    }
  };

  const updateHeaderCellWidths: Function = (): void => {
    if (headerCellElements && Object.keys(headerCellElements).length) {
      let previousPositionOffset: number = 0;
      Object.entries(dividerOffsets).forEach(([itemId, offsetValue]: [string, string]): void => {
        const offsetNumberValue: number = FormatService.parseInt(offsetValue);
        const headerWidth: number = offsetNumberValue - previousPositionOffset;
        previousPositionOffset = offsetNumberValue;

        const headerCell: HTMLElement = headerCellElements[itemId];
        if (headerCell) {
          headerCell.style.width = `${headerWidth}px`;
        }
      });
    }
  };

  const updateTableWidth: Function = (): void => {
    if (tableElement) {
      tableElement.style.width = `${tableWidth}px`;
    }
  };

  const updateTable: Function = (): void => {
    if (!tableElement || !headerCellElements || !Object.keys(headerCellElements).length) {
      return;
    }

    const minHeaderCellWidth: number = minCellWidth
      || (isDisabled ? MIN_HEADER_CELL_WIDTH_DISABLED : MIN_HEADER_CELL_WIDTH);
    const stableDividerOffsets: Record<string, StructureItem> = activeItemId ? { ...currentDividerOffsets } : {};
    const tablePosition: DOMRect = tableElement.getBoundingClientRect();
    const headerCells: [string, HTMLElement][] = Object.entries(headerCellElements)
      .filter(([itemId, headerCell]: [string, HTMLElement]): boolean => (
        !!itemId && !!headerCell
      ));
    const visibleTableColumnSettings: TableField[] = tableColumnSettings && tableColumnSettings
      .filter((tableField: TableField): boolean => !!tableField)
      .map((tableField: TableField): TableField | null => tableField.isVisible ? tableField : null);

    let previousCellsWidth: number = 0;
    let currentOffset: number = 0;
    let tableWidthValue: number = 0;
    headerCells.forEach(([itemId, headerCell]: [string, HTMLElement], headerCellIndex: number): void => {
      if (headerCell) {
        const headerCellPosition: DOMRect = headerCell.getBoundingClientRect();

        if (headerCellIndex === headerCells.length - 1) {
          const dividerOffsetValues: Record<string, StructureItem> = activeItemId
            ? dividerOffsets : stableDividerOffsets;
          const lastOffsetId: string = Object.keys(dividerOffsetValues)[Object.keys(dividerOffsetValues).length - 1];
          tableWidthValue = dividerOffsetValues[lastOffsetId] + minHeaderCellWidth;

          return;
        }

        if (activeItemId) {
          if (FormatService.parseInt(itemId) === FormatService.parseInt(activeItemId)) {
            const previousItemId: string = Object.keys(stableDividerOffsets)[headerCellIndex - 1];
            const previousItemOffset: number = stableDividerOffsets[previousItemId] || 0;
            let currentOffsetPosition: number = activeItemOffset - tablePosition.left;
            currentOffsetPosition = currentOffsetPosition < previousItemOffset + minHeaderCellWidth
              ? previousItemOffset + minHeaderCellWidth : currentOffsetPosition;
            currentOffsetPosition = currentOffsetPosition > previousItemOffset + MAX_HEADER_CELL_WIDTH
              ? previousItemOffset + MAX_HEADER_CELL_WIDTH : currentOffsetPosition;
            currentOffset = currentOffsetPosition - (currentActiveItemOffset - tablePosition.left);

            stableDividerOffsets[itemId] = currentOffsetPosition;
          } else if (FormatService.parseInt(itemId) > FormatService.parseInt(activeItemId)) {
            stableDividerOffsets[itemId] = (currentDividerOffsets[itemId] || minHeaderCellWidth) + currentOffset;
          }
        } else {
          const visibleColumnSettingWidth: number | null = (visibleTableColumnSettings
            && visibleTableColumnSettings[FormatService.parseInt(itemId)]
            && visibleTableColumnSettings[FormatService.parseInt(itemId)].width) || null;

          if (isDisabled) {
            const currentOffsetPosition: number = previousCellsWidth + minHeaderCellWidth;
            stableDividerOffsets[itemId] = currentOffsetPosition;
            previousCellsWidth += (currentOffsetPosition - previousCellsWidth);
          } else if (visibleColumnSettingWidth) {
            stableDividerOffsets[itemId] = visibleColumnSettingWidth + previousCellsWidth;
            previousCellsWidth += visibleColumnSettingWidth;
          } else {
            let currentOffsetPosition: number = headerCellPosition.left + headerCellPosition.width - tablePosition.left;
            currentOffsetPosition = currentOffsetPosition - previousCellsWidth < minHeaderCellWidth
              ? previousCellsWidth + minHeaderCellWidth : currentOffsetPosition;
            currentOffsetPosition = currentOffsetPosition - previousCellsWidth > MAX_HEADER_CELL_WIDTH
              ? previousCellsWidth + MAX_HEADER_CELL_WIDTH : currentOffsetPosition;

            stableDividerOffsets[itemId] = currentOffsetPosition;
            previousCellsWidth += (currentOffsetPosition - previousCellsWidth);
          }
        }
      }
    });

    setDividerHeight(tablePosition.height);
    setTableWidth(tableWidthValue);
    setDividerOffsets({ ...stableDividerOffsets });
  };

  const onResizeStart: Function = (itemId: string, offsetValue: number): void => {
    setIsResize(true);
    setActiveItemId(itemId);
    setActiveItemOffset(offsetValue);
    setCurrentActiveItemOffset(offsetValue);
    setCurrentDividerOffsets({ ...dividerOffsets });
  };

  const onResizeMove: Function = (offsetValue: number): void => {
    setActiveItemOffset(offsetValue);

    updateTable();
  };

  const onResizeEnd: Function = (): void => {
    setIsResize(false);
    setActiveItemId(null);
    setActiveItemOffset(0);
    setCurrentActiveItemOffset(0);
    setCurrentDividerOffsets({});

    if (activeItemOffset) {
      updateTableColumnSettings();
    }
  };

  useEffect((): void => {
    updateHeaderCellWidths();
  }, [dividerOffsets]);

  useEffect((): void => {
    updateTableWidth();
  }, [tableWidth]);

  useEffect((): void => {
    updateTableVisibleColumns();
  }, [tableColumnSettings]);

  useEffect((): ReactUseEffectCleanUp => {
    const timeoutId: number | null = setTimeout(updateTable, RENDER_DELAY);

    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [tableVisibleColumns]);

  useEffect((): ReactUseEffectCleanUp => {
    let timeout: number | undefined;

    if (!isDisabled && !isActive) {
      timeout = setTimeout(updateTable, TIMEOUT_DEFAULT);
    } else {
      updateTable();
    }

    return (): void => {
      clearTimeout(timeout);
    };
  }, [tableElement, headerCellElements, headerCellElementsTotal, isActive, isDisabled]);

  return (
    <div>
      {
        isActive && (
          <div
            className={classes.tableResizeContainer}
            style={{
              width: tableWidth || WIDTH_AUTO,
            }}
          >
            {
              Object.entries(dividerOffsets).map(([itemId, offsetValue]: [string, number]): ReactElement => (
                <div
                  key={itemId}
                  className={classes.tableResizeHandleBlock}
                  style={{
                    width: isResize && activeItemId === itemId && tableWidth ? tableWidth : 0,
                    height: dividerHeight,
                    position: isResize && activeItemId === itemId ? 'absolute' : 'relative',
                  }}
                  onMouseMove={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    if (isResize) {
                      onResizeMove(event.clientX);
                    }
                  }}
                  onMouseUp={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    if (event.button === MOUSE_BUTTON_LEFT) {
                      onResizeEnd();
                    }
                  }}
                >
                  <div
                    className={classes.tableResizeHandle}
                    style={{
                      left: offsetValue || 0,
                    }}
                    onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                      if (event.button === MOUSE_BUTTON_LEFT) {
                        onResizeStart(itemId, event.clientX);
                      }
                    }}
                  />
                </div>
              ))
            }
          </div>
        )
      }
      <div
        style={{
          visibility: isLoading ? VISIBILITY_HIDDEN : VISIBILITY_VISIBLE,
          height: isLoading ? 0 : HEIGHT_AUTO,
        }}
      >
        {children}
      </div>
    </div>
  );
});

export default TableResize;
